import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import InvoiceDetailView from './InvoiceDetailView';
import { 
  Search, 
  Download, 
  Eye, 
  CheckCircle, 
  Clock, 
  XCircle, 
  ThumbsUp, 
  ThumbsDown, 
  AlertCircle, 
  Filter, 
  Package, 
  Calendar,
  FileText,
  Building2,
  DollarSign,
  ArrowUpRight,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const purchaseInvoices = [
  { 
    number: 'PINV-2024-892', 
    date: '2025-10-24',
    month: '2025-10',
    dueDate: '2025-11-24',
    vendor: 'Global Supplies LLC',
    vendorTRN: 'AE987654321',
    items: [
      { description: 'Office Furniture', quantity: 1, unitPrice: 8333.33, taxRate: 5, amount: 8750 }
    ],
    subtotal: 8333.33,
    tax: 416.67,
    amount: 8750,
    status: 'accepted',
    receivedFrom: 'ServicePro UAE',
    receivedDate: '2025-10-24 09:30:00',
    reviewedBy: 'admin@abctrading.ae',
    reviewedDate: '2025-10-24 10:15:00'
  },
  { 
    number: 'PINV-2024-891', 
    date: '2025-10-23',
    month: '2025-10',
    dueDate: '2025-11-23',
    vendor: 'Office Supplies Co',
    vendorTRN: 'AE456789123',
    items: [
      { description: 'Stationery Items', quantity: 50, unitPrice: 66.29, taxRate: 5, amount: 3480 }
    ],
    subtotal: 3314.29,
    tax: 165.71,
    amount: 3480,
    status: 'rejected',
    receivedFrom: 'ServicePro UAE',
    receivedDate: '2025-10-23 14:20:00',
    reviewedBy: 'admin@abctrading.ae',
    reviewedDate: '2025-10-23 15:10:00',
    rejectionReason: 'Incorrect pricing - does not match purchase order'
  },
  { 
    number: 'PINV-2024-890', 
    date: '2025-10-23',
    month: '2025-10',
    dueDate: '2025-11-23',
    vendor: 'Tech Equipment FZ',
    vendorTRN: 'AE789123456',
    items: [
      { description: 'Laptop Computer', quantity: 10, unitPrice: 1485.71, taxRate: 5, amount: 15600 }
    ],
    subtotal: 14857.14,
    tax: 742.86,
    amount: 15600,
    status: 'pending',
    receivedFrom: 'ServicePro UAE',
    receivedDate: '2025-10-23 16:45:00'
  },
  { 
    number: 'PINV-2024-889', 
    date: '2025-10-22',
    month: '2025-10',
    dueDate: '2025-11-22',
    vendor: 'Industrial Parts LLC',
    vendorTRN: 'AE321654987',
    items: [
      { description: 'Machine Components', quantity: 5, unitPrice: 4266.67, taxRate: 5, amount: 22400 }
    ],
    subtotal: 21333.33,
    tax: 1066.67,
    amount: 22400,
    status: 'pending',
    receivedFrom: 'ServicePro UAE',
    receivedDate: '2025-10-22 11:30:00'
  },
  { 
    number: 'PINV-2024-875', 
    date: '2025-09-15',
    month: '2025-09',
    dueDate: '2025-10-15',
    vendor: 'Office Supplies Co',
    vendorTRN: 'AE456789123',
    items: [
      { description: 'Printer Supplies', quantity: 20, unitPrice: 150, taxRate: 5, amount: 3150 }
    ],
    subtotal: 3000,
    tax: 150,
    amount: 3150,
    status: 'accepted',
    receivedFrom: 'ServicePro UAE',
    receivedDate: '2025-09-15 11:00:00',
    reviewedBy: 'admin@abctrading.ae',
    reviewedDate: '2025-09-15 14:30:00'
  },
  { 
    number: 'PINV-2024-860', 
    date: '2025-08-20',
    month: '2025-08',
    dueDate: '2025-09-20',
    vendor: 'Global Supplies LLC',
    vendorTRN: 'AE987654321',
    items: [
      { description: 'Cleaning Supplies', quantity: 50, unitPrice: 42, taxRate: 5, amount: 2205 }
    ],
    subtotal: 2100,
    tax: 105,
    amount: 2205,
    status: 'accepted',
    receivedFrom: 'ServicePro UAE',
    receivedDate: '2025-08-20 10:15:00',
    reviewedBy: 'admin@abctrading.ae',
    reviewedDate: '2025-08-20 16:00:00'
  },
];

export default function PurchaseInvoices() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [isDetailView, setIsDetailView] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'accept' | 'reject'>('accept');
  const [rejectionReason, setRejectionReason] = useState('');
  const [invoices, setInvoices] = useState(purchaseInvoices);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [isBulkActionOpen, setIsBulkActionOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'accept' | 'reject'>('accept');
  const [bulkRejectionReason, setBulkRejectionReason] = useState('');

  // Get unique months from invoices
  const availableMonths = Array.from(new Set(invoices.map(inv => inv.month))).sort().reverse();

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.vendor.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMonth = selectedMonth === 'all' || invoice.month === selectedMonth;
    return matchesSearch && matchesMonth;
  });

  const pendingCount = invoices.filter(inv => inv.status === 'pending').length;
  const acceptedCount = invoices.filter(inv => inv.status === 'accepted').length;
  const rejectedCount = invoices.filter(inv => inv.status === 'rejected').length;
  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'accepted':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            Accepted
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">
            <Clock className="w-3 h-3 mr-1" />
            Pending Review
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-100">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleViewInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setIsDetailView(true);
  };

  const handleBackToList = () => {
    setIsDetailView(false);
    setSelectedInvoice(null);
  };

  const handleAcceptReject = (action: 'accept' | 'reject') => {
    setConfirmAction(action);
    setIsConfirmOpen(true);
  };

  const handleConfirmAction = () => {
    if (!selectedInvoice) return;

    const updatedInvoices = invoices.map(inv => {
      if (inv.number === selectedInvoice.number) {
        return {
          ...inv,
          status: confirmAction === 'accept' ? 'accepted' : 'rejected',
          reviewedBy: 'admin@abctrading.ae',
          reviewedDate: new Date().toISOString().slice(0, 19).replace('T', ' '),
          rejectionReason: confirmAction === 'reject' ? rejectionReason : undefined
        };
      }
      return inv;
    });

    setInvoices(updatedInvoices);
    setSelectedInvoice({
      ...selectedInvoice,
      status: confirmAction === 'accept' ? 'accepted' : 'rejected',
      reviewedBy: 'admin@abctrading.ae',
      reviewedDate: new Date().toISOString().slice(0, 19).replace('T', ' '),
      rejectionReason: confirmAction === 'reject' ? rejectionReason : undefined
    });

    toast.success(`Invoice ${confirmAction === 'accept' ? 'accepted' : 'rejected'} and status sent to Service Provider`);
    setIsConfirmOpen(false);
    setRejectionReason('');
  };

  const handleQuickAction = (invoiceNumber: string, action: 'accept' | 'reject') => {
    const invoice = invoices.find(inv => inv.number === invoiceNumber);
    if (!invoice) return;

    if (action === 'reject') {
      // Open rejection dialog with pre-selected invoice
      setSelectedInvoice(invoice);
      setConfirmAction('reject');
      setIsConfirmOpen(true);
    } else {
      // Quick accept
      const updatedInvoices = invoices.map(inv => {
        if (inv.number === invoiceNumber) {
          return {
            ...inv,
            status: 'accepted',
            reviewedBy: 'admin@abctrading.ae',
            reviewedDate: new Date().toISOString().slice(0, 19).replace('T', ' ')
          };
        }
        return inv;
      });

      setInvoices(updatedInvoices);
      toast.success(`Invoice ${invoiceNumber} accepted and status sent to Service Provider`);
    }
  };

  const handleSelectInvoice = (invoiceNumber: string, checked: boolean) => {
    if (checked) {
      setSelectedInvoices([...selectedInvoices, invoiceNumber]);
    } else {
      setSelectedInvoices(selectedInvoices.filter(num => num !== invoiceNumber));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const pendingInvoiceNumbers = filteredInvoices
        .filter(inv => inv.status === 'pending')
        .map(inv => inv.number);
      setSelectedInvoices(pendingInvoiceNumbers);
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleBulkAction = (action: 'accept' | 'reject') => {
    setBulkAction(action);
    setIsBulkActionOpen(true);
  };

  const handleConfirmBulkAction = () => {
    const updatedInvoices = invoices.map(inv => {
      if (selectedInvoices.includes(inv.number)) {
        return {
          ...inv,
          status: bulkAction === 'accept' ? 'accepted' : 'rejected',
          reviewedBy: 'admin@abctrading.ae',
          reviewedDate: new Date().toISOString().slice(0, 19).replace('T', ' '),
          rejectionReason: bulkAction === 'reject' ? bulkRejectionReason : undefined
        };
      }
      return inv;
    });

    setInvoices(updatedInvoices);
    toast.success(`${selectedInvoices.length} invoice(s) ${bulkAction === 'accept' ? 'accepted' : 'rejected'} and status sent to Service Provider`);
    setSelectedInvoices([]);
    setIsBulkActionOpen(false);
    setBulkRejectionReason('');
  };

  const handleExport = () => {
    toast.success('Purchase invoices exported successfully');
  };

  const formatMonthDisplay = (month: string) => {
    const date = new Date(month + '-01');
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Show detail view if an invoice is selected
  if (isDetailView && selectedInvoice) {
    return (
      <InvoiceDetailView 
        invoice={{ ...selectedInvoice, supplier: selectedInvoice.vendor }}
        onBack={handleBackToList}
        type="purchase"
      />
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 bg-gradient-to-br from-slate-50 to-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#0052CC] rounded-lg">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl text-gray-900">Purchase Invoices</h1>
              <p className="text-gray-600 mt-1">Review and manage purchase invoices received from Service Provider</p>
            </div>
          </div>
        </div>
        <Button className="bg-[#0052CC] hover:bg-[#0041A3]" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Export Data
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-[#0052CC] shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Invoices</p>
                <p className="text-3xl text-gray-900">{invoices.length}</p>
                <p className="text-xs text-gray-500 mt-2">AED {totalAmount.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <FileText className="w-5 h-5 text-[#0052CC]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Pending Review</p>
                <p className="text-3xl text-amber-600">{pendingCount}</p>
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Requires Action
                </p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Accepted</p>
                <p className="text-3xl text-emerald-600">{acceptedCount}</p>
                <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                  <ArrowUpRight className="w-3 h-3" />
                  Approved
                </p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Tax (VAT)</p>
                <p className="text-3xl text-purple-600">
                  {invoices.reduce((sum, inv) => sum + inv.tax, 0).toLocaleString()}
                </p>
                <p className="text-xs text-purple-600 mt-2">AED</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Info */}
      <Card className="border-[#0052CC]/20 shadow-sm">
        <CardContent className="p-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm mt-0.5">
                <AlertCircle className="w-5 h-5 text-[#0052CC]" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900 mb-3"><strong>Purchase Invoice Workflow:</strong></p>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 bg-[#0052CC] text-white rounded-full flex items-center justify-center text-xs">1</div>
                      <p className="text-xs text-gray-900">Vendor Submits</p>
                    </div>
                    <p className="text-xs text-gray-600">To Service Provider</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 bg-[#0052CC] text-white rounded-full flex items-center justify-center text-xs">2</div>
                      <p className="text-xs text-gray-900">Forwarded</p>
                    </div>
                    <p className="text-xs text-gray-600">To AIS for review</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm border border-amber-200 bg-amber-50">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 bg-amber-600 text-white rounded-full flex items-center justify-center text-xs">3</div>
                      <p className="text-xs text-gray-900">Your Review</p>
                    </div>
                    <p className="text-xs text-gray-700">Accept or Reject</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 bg-[#0052CC] text-white rounded-full flex items-center justify-center text-xs">4</div>
                      <p className="text-xs text-gray-900">Status Sent</p>
                    </div>
                    <p className="text-xs text-gray-600">Via API to Provider</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 bg-[#0052CC] text-white rounded-full flex items-center justify-center text-xs">5</div>
                      <p className="text-xs text-gray-900">Processed</p>
                    </div>
                    <p className="text-xs text-gray-600">Based on decision</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search and Filters */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-1 relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by invoice number or vendor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="w-full md:w-64">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="flex-1 text-left">{selectedMonth === 'all' ? 'All Months' : formatMonthDisplay(selectedMonth)}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Months</SelectItem>
                  {availableMonths.map(month => (
                    <SelectItem key={month} value={month}>
                      {formatMonthDisplay(month)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedInvoices.length > 0 && (
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-[#0052CC]" />
                </div>
                <div>
                  <p className="text-gray-900">
                    <strong>{selectedInvoices.length}</strong> invoice(s) selected
                  </p>
                  <p className="text-xs text-gray-600">Ready for bulk action</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedInvoices([])}
                  className="bg-white"
                >
                  Clear Selection
                </Button>
                <Button 
                  variant="outline" 
                  className="border-rose-600 text-rose-700 hover:bg-rose-50 bg-white"
                  size="sm"
                  onClick={() => handleBulkAction('reject')}
                >
                  <ThumbsDown className="w-4 h-4 mr-2" />
                  Bulk Reject
                </Button>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700 shadow-sm"
                  size="sm"
                  onClick={() => handleBulkAction('accept')}
                >
                  <ThumbsUp className="w-4 h-4 mr-2" />
                  Bulk Accept
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices Table */}
      <Card className="shadow-sm">
        <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-slate-50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#0052CC]" />
                Purchase Invoices
              </CardTitle>
              <CardDescription className="mt-1">
                Showing {filteredInvoices.length} of {invoices.length} invoices
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedInvoices.length > 0 && selectedInvoices.length === filteredInvoices.filter(inv => inv.status === 'pending').length}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Amount (AED)</TableHead>
                  <TableHead>Received From</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2 text-gray-500">
                        <Package className="w-12 h-12 text-gray-300" />
                        <p>No invoices found</p>
                        <p className="text-sm">Try adjusting your search or filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.number} className="hover:bg-gray-50/50">
                      <TableCell>
                        {invoice.status === 'pending' && (
                          <Checkbox
                            checked={selectedInvoices.includes(invoice.number)}
                            onCheckedChange={(checked) => handleSelectInvoice(invoice.number, checked as boolean)}
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-[#0052CC]">{invoice.number}</TableCell>
                      <TableCell className="text-gray-700">{invoice.date}</TableCell>
                      <TableCell className="text-gray-600 text-sm">{formatMonthDisplay(invoice.month)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-900">{invoice.vendor}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-gray-900">{invoice.amount.toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
                          {invoice.receivedFrom}
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {invoice.status === 'pending' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-8 w-8 p-0"
                                onClick={() => handleQuickAction(invoice.number, 'accept')}
                                title="Accept"
                              >
                                <ThumbsUp className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-8 w-8 p-0"
                                onClick={() => handleQuickAction(invoice.number, 'reject')}
                                title="Reject"
                              >
                                <ThumbsDown className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-[#0052CC] hover:text-[#0041A3] hover:bg-blue-50 h-8 w-8 p-0"
                            onClick={() => handleViewInvoice(invoice)}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmAction === 'accept' ? (
                <>
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  Accept Invoice
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-rose-600" />
                  Reject Invoice
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {confirmAction === 'accept' 
                ? 'Confirm that you want to accept this purchase invoice. Status will be sent to Service Provider.'
                : 'Provide a reason for rejecting this purchase invoice. Status will be sent to Service Provider.'
              }
            </DialogDescription>
          </DialogHeader>

          {confirmAction === 'reject' && (
            <div className="space-y-2">
              <label className="text-sm text-gray-700">Rejection Reason *</label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter detailed reason for rejection..."
                className="mt-1"
                rows={4}
              />
              <p className="text-xs text-gray-500">This reason will be shared with the service provider.</p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => {
              setIsConfirmOpen(false);
              setRejectionReason('');
            }}>Cancel</Button>
            <Button 
              className={confirmAction === 'accept' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}
              onClick={handleConfirmAction}
              disabled={confirmAction === 'reject' && !rejectionReason.trim()}
            >
              {confirmAction === 'accept' ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm Accept
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Confirm Reject
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={isBulkActionOpen} onOpenChange={setIsBulkActionOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {bulkAction === 'accept' ? (
                <>
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  Bulk Accept Invoices
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-rose-600" />
                  Bulk Reject Invoices
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === 'accept' 
                ? `You are about to accept ${selectedInvoices.length} invoice(s). Status will be sent to Service Provider for all selected invoices.`
                : `You are about to reject ${selectedInvoices.length} invoice(s). Provide a reason that will apply to all selected invoices.`
              }
            </DialogDescription>
          </DialogHeader>

          {bulkAction === 'reject' && (
            <div className="space-y-2">
              <label className="text-sm text-gray-700">Rejection Reason (applies to all) *</label>
              <Textarea
                value={bulkRejectionReason}
                onChange={(e) => setBulkRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                className="mt-1"
                rows={3}
              />
            </div>
          )}

          <div className="bg-gray-50 border rounded-lg p-4 max-h-48 overflow-y-auto">
            <p className="text-sm text-gray-700 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-500" />
              <strong>Selected Invoices ({selectedInvoices.length}):</strong>
            </p>
            <div className="space-y-2">
              {selectedInvoices.map(invNum => {
                const inv = invoices.find(i => i.number === invNum);
                return (
                  <div key={invNum} className="flex items-center justify-between p-2 bg-white rounded border border-gray-200 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-[#0052CC] rounded-full"></div>
                      <span className="text-gray-900">{invNum}</span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-600">{inv?.vendor}</span>
                    </div>
                    <span className="text-gray-900">AED {inv?.amount.toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => {
              setIsBulkActionOpen(false);
              setBulkRejectionReason('');
            }}>Cancel</Button>
            <Button 
              className={bulkAction === 'accept' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}
              onClick={handleConfirmBulkAction}
              disabled={bulkAction === 'reject' && !bulkRejectionReason.trim()}
            >
              {bulkAction === 'accept' ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Accept {selectedInvoices.length} Invoice(s)
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject {selectedInvoices.length} Invoice(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
